import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import * as _ from "lodash";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import categoryService from "app/common/services/categoryService";
import { chosenSites } from "app/common/services/chosenSites";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { ChannelsOverviewBarChartTopLegends } from "app/components/React/ChannelsOverviewBarChartTopLegends/ChannelsOverviewBarChartTopLegends";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { minAbbrNumberFilter, minVisitsAbbrFilter } from "app/filters/ngFilters";
import type { ISiteLegendItem } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartContainer";
import { ChannelsOverviewBarChart } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChart";
import {
    getCategoryDataPoints,
    getChartConfig,
    getPointY,
    translateSourceText,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartConfig";
import {
    BoxContainer,
    BoxSubtitleWrapper,
    LegendsSingleWrapper,
    LoaderWrapper,
    NoDataWrapper,
    StyledHeaderTitle,
    TitleBox,
    TotalTrafficNumber,
    TotalTrafficTitle,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/StyledComponents";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { isHasNoWWPermissions } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import utils, { orderedTrafficSources } from "app/Shared/utils";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import type { ValueOf } from "pro-features/types";
import type { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

const WidgetFooterContainer = styled.div`
    height: 51px;
    border-top: 1px solid ${colorsPalettes.carbon["50"]};
    padding-right: 24px;
    padding-left: 24px;
    display: flex;
    flex-direction: row-reverse;
    flex-shrink: 0;
    align-items: center;
`;

const StyledBoxContainer = styled(BoxContainer)`
    height: auto;
    margin-bottom: 0;
`;
export const PERCENT_DATA_FORMAT = "%";
export const NUMBERS_DATA_FORMAT = "#";

interface ILegendItem extends Partial<ISiteLegendItem> {
    isCategory?: boolean;
    data: number;
    visible: boolean;
}

export const ChannelsOverviewBarChartContainer: React.FC<{
    categoryBenchmark?: boolean;
    getCtaLink: (params) => { href: string; target: string };
    unlockHook?: any;
    hideFooter?: boolean;
}> = ({ categoryBenchmark, getCtaLink, unlockHook = null, hideFooter = false }) => {
    const fetchService = DefaultFetchService.getInstance();
    const params = proNavigator.getParams();
    const { country, comparedDuration, isWWW, key } = params;
    const webSource: ValueOf<typeof devicesTypes> = "Desktop";
    ////
    const category = chosenSites.getInfo(params.key).category?.replace("/", "~").split(" ").join("_");
    const isWWOApiController = isHasNoWWPermissions(country);
    const ctrl = isWWOApiController ? "WorldwideOverviewDesktop" : "WebsiteOverviewDesktop";
    const duration = isWWOApiController ? BasicDurations.LAST_THREE_MONTHS : params.duration;

    const ctaLink = getCtaLink({
        ...params,
        webSource: webSource,
        category: category,
    });
    ////
    const i18n = i18nFilter();
    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );
    const fromDate = durationObject.raw.from;
    const toDate = durationObject.raw.to;
    const { from, to, isWindow } = durationObject.forAPI;
    const is28d = duration === "28d";
    const isCompare = key.split(",").length > 1;
    const includeSubDomains = isWWW === "*";
    const timeGranularity = is28d ? "Daily" : "Monthly";

    const [data, setData] = useState<any>(null);
    const [series, setSeries] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const [legends, setLegends] = useState<ILegendItem[]>(
        chosenSites.sitelistForLegend().map((item) => ({
            name: item.name,
            color: item.color,
            visible: true,
            data: null,
        })),
    );
    const chartRef = useRef<HTMLElement>(null);
    const visualizations = [PERCENT_DATA_FORMAT, NUMBERS_DATA_FORMAT];
    const [selectedVisualizationIndex, setSelectedVisualizationIndex] = useState<number>(0);

    const [selectedCategory, setSelectedCategory] = useState<object>(
        categoryBenchmark && category && categoryService.getCategory(category),
    );
    const isCategorySelected = useMemo(
        () => selectedCategory && selectedCategory["id"] !== "no-category",
        [selectedCategory],
    );

    useEffect(() => {
        async function fetchData() {
            setData(null);
            setSeries(null);
            setIsLoading(true);

            const dataPromise = fetchService
                .get<{ Data: object[] }>(`widgetApi/${ctrl}/TrafficSourcesOverview/PieChart`, {
                    country,
                    from,
                    includeSubDomains,
                    isWindow,
                    keys: key,
                    timeGranularity,
                    to,
                    webSource,
                })
                .then((res) => res.Data);

            const promises = [dataPromise];
            if (isCategorySelected) {
                const industryKey = "$" + selectedCategory["id"];
                const industryDataPromise = fetchService
                    .get<{ Data: object[] }>(
                        isWWOApiController
                            ? "api/WidgetKpis/WorldWide/TrafficSourcesOverviewAverage/GetPieChartData"
                            : "api/WidgetKpis/TrafficSourcesOverviewAverage/GetPieChartData",
                        {
                            country,
                            from,
                            includeSubDomains,
                            isWindow,
                            keys: industryKey,
                            timeGranularity,
                            to,
                            webSource,
                        },
                    )
                    .then((industryResponse: any) => industryResponse.Data[industryKey]);

                promises.push(industryDataPromise);
            }
            try {
                const [mmXTrafficSources, industryTrafficSources] = await Promise.all(promises);
                setData(transformData(mmXTrafficSources, industryTrafficSources));
            } catch (e) {
                setIsError(true);
                setIsLoading(false);
            }
        }

        fetchData();
    }, [from, to, isWindow, country, webSource, key, selectedCategory]);

    useEffect(() => {
        if (data) {
            const updatedLegends = [];
            legends.forEach((legend) => {
                if (legend?.isCategory) {
                    if (isCategorySelected) {
                        updatedLegends.push(legend);
                    }
                } else {
                    updatedLegends.push({
                        ...legend,
                        data: data?.[legend.name]?.totalVisits
                            ? minVisitsAbbrFilter()(data?.[legend.name]?.totalVisits)
                            : "N/A",
                    });
                }
            });

            setLegends(updatedLegends);
        }
    }, [data]);

    useEffect(() => {
        // getting JQuery element
        const $chart = $(chartRef.current);
        $chart.on("click", "[data-ts-category]", (evt) => {
            const trafficSource = evt.target.getAttribute("data-ts-category");
            allTrackers.trackEvent("Internal Link", "click", `Traffic Sources Overview/${trafficSource}`);
        });

        return () => {
            $chart.off("click", "[data-ts-category]");
        };
    }, []);

    const addCategoryLegend = (name, total) => {
        const categoryLegend = {
            data: minAbbrNumberFilter()(total),
            visible: true,
            name,
            color: "rgb(216, 220, 232)",
            isCategory: true,
        };

        setLegends([...legends.filter((l) => !l.isCategory), categoryLegend]);
    };

    useEffect(() => {
        if (data) {
            if (_.isEmpty(data)) {
                setSeries({});
                setIsLoading(false);
            } else {
                const sitesDataPoints = [];
                key.split(",").forEach((domainName) => {
                    if (data[domainName]) {
                        const { trafficSources, totalVisits } = data[domainName];

                        if (legends.find((l: ILegendItem) => l.name === domainName)?.visible) {
                            sitesDataPoints.push({
                                name: domainName,
                                data: orderedTrafficSources
                                    .filter((source) => trafficSources.hasOwnProperty(source))
                                    .map((sourceName) => ({
                                        key: translateSourceText(sourceName),
                                        y: getPointY(
                                            trafficSources[sourceName],
                                            totalVisits,
                                            visualizations[selectedVisualizationIndex],
                                        ),
                                        color: isCompare
                                            ? chosenSites.getSiteColor(domainName)
                                            : CHART_COLORS.trafficSourcesColorsBySourceMMX[sourceName],
                                    })),
                                id: domainName,
                            });
                        }
                    }
                });

                let categoryDataPoints = [];
                if (data.category && legends.find((l: ILegendItem) => l.name === selectedCategory["text"])?.visible) {
                    categoryDataPoints = getCategoryDataPoints(
                        data,
                        visualizations[selectedVisualizationIndex],
                        sitesDataPoints.length,
                    );
                }
                setSeries([...sitesDataPoints, ...categoryDataPoints]);
                setIsLoading(false);
            }
        }
    }, [data, selectedVisualizationIndex, legends]);

    const config = useMemo(
        () =>
            data &&
            getChartConfig({
                data,
                dataFormat: visualizations[selectedVisualizationIndex],
                isCompare,
                isBenchmarkMode: isCategorySelected,
                showWatermark: true,
            }),
        [data, webSource, isCompare, key, selectedVisualizationIndex, isCategorySelected],
    );

    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: duration !== "28d" && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ],
        [fromDate, toDate, country, webSource, duration],
    );

    const getTotalVisits = (data) => {
        return Object.values<number>(data).reduce((sum: number, val) => sum + val, 0);
    };

    const checkAreTrafficSourcesEmpty = (mmXTrafficSources) => {
        let areTrafficSourcesEmpty = true;
        const sourcesValuesArray = Object.values(mmXTrafficSources);
        let i = 0;
        while (i < sourcesValuesArray.length && areTrafficSourcesEmpty) {
            if (sourcesValuesArray[i]) {
                areTrafficSourcesEmpty = false;
            }
            i++;
        }
        return areTrafficSourcesEmpty;
    };

    const transformData = (mmXTrafficSources, industryTrafficSources) => {
        const sitesDataWithTotalVisits = checkAreTrafficSourcesEmpty(mmXTrafficSources)
            ? {}
            : Object.entries(mmXTrafficSources).reduce((newDataObject, [domain, trafficSources]) => {
                  trafficSources = trafficSources || {};
                  const totalVisits = getTotalVisits(trafficSources);

                  return {
                      ...newDataObject,
                      [domain]: {
                          trafficSources: trafficSources,
                          totalVisits: totalVisits,
                      },
                  };
              }, {});

        const res = {
            Data: {
                ...sitesDataWithTotalVisits,
            },
        };

        if (industryTrafficSources) {
            const total = getTotalVisits(industryTrafficSources);
            res.Data["category"] = {
                ...selectedCategory,
                trafficSources: industryTrafficSources,
                totalVisits: total,
            };

            addCategoryLegend(selectedCategory["text"], total);
        }
        return _.isEmpty(res.Data) ? {} : utils.fillMissingTrafficSources(res.Data, false, params.duration);
    };

    const getLegends = () => {
        const onLegendClick = (filter) => {
            const action = filter.visible ? "remove" : "click";
            const filterSites = legends.map((f) => {
                if (f.name === filter.name) {
                    f.visible = !f.visible;
                }
                return f;
            });

            setLegends(filterSites);
            allTrackers.trackEvent("Click Filter", action, `Bar Chart/Traffic Sources Overview/${filter.name}`);
        };
        return (
            legends &&
            legends.length > 0 && (
                <ChannelsOverviewBarChartTopLegends
                    legendItems={legends}
                    onLegendsChange={onLegendClick}
                />
            )
        );
    };

    const getComponent = () => {
        if (!data || _.isEmpty(data) || isError) {
            return (
                <NoDataWrapper>
                    <NoDataLandscape
                        title="mmx.channelsoverview.bar.chart.no.data.title"
                        subtitle="mmx.channelsoverview.bar.chart.no.data.subtitle"
                    />
                </NoDataWrapper>
            );
        }

        const isSingleMode = !isCategorySelected && !isCompare;
        return (
            <>
                <Flex
                    flexDirection="column"
                    gap="16px"
                    padding="24px 24px 0 24px"
                >
                    {isSingleMode
                        ? data?.[key]?.totalVisits && (
                              <LegendsSingleWrapper>
                                  <TotalTrafficTitle>
                                      {i18n("mmx.channelsoverview.bar.chart.total.traffic.title")}
                                  </TotalTrafficTitle>
                                  <TotalTrafficNumber>
                                      {minAbbrNumberFilter()(data[key].totalVisits)}
                                  </TotalTrafficNumber>
                              </LegendsSingleWrapper>
                          )
                        : getLegends()}
                    <ChannelsOverviewBarChart
                        data={series}
                        config={config}
                        dataAutomation="ChannelsOverviewBarChart"
                        stackColumns={false}
                    />
                </Flex>
                {hideFooter ? null : (
                    <WidgetFooterContainer>
                        <FooterComponent
                            href={ctaLink.href}
                            onClick={trackLink}
                            text="wwo.traffic.sources.button"
                            unlockHook={unlockHook}
                            target={ctaLink.target}
                        />
                    </WidgetFooterContainer>
                )}
            </>
        );
    };
    const trackLink = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Marketing Mix/Channels Overview/see full overview");
    };
    return (
        <StyledBoxContainer
            ref={chartRef}
            data-automation={"channelsOverviewContainer"}
        >
            <TitleBox>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18n("mmx.trafficSources.bar.tooltip")}>
                            {i18n("mmx.trafficSources.bar.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <BoxSubtitleWrapper>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                    </BoxSubtitleWrapper>
                </FlexColumn>
            </TitleBox>
            {isLoading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                getComponent()
            )}
        </StyledBoxContainer>
    );
};
